import React, { useState, useRef, useEffect, useCallback } from 'react';
import merge from 'lodash/merge';
import { useWindowSize } from '../hooks';

interface Props {
  children: (props: { height: number }) => React.ReactElement;
  paddingBottom?: number;
  className?: string;
  style?: React.CSSProperties;
}

export const FullScreen = ({ children, paddingBottom = 0, className = '', style: styleProp = {} }: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  const windowSize = useWindowSize();

  const calculateAndSetHeight = useCallback(() => {
    if (ref.current) {
      const { top } = ref.current.getBoundingClientRect();
      const h = windowSize.height - top - paddingBottom;
      setHeight(h > 0 ? h : 0);
    }
  }, [windowSize.height, paddingBottom]);

  // Recompute when dependencies of the calculation change.
  useEffect(() => {
    calculateAndSetHeight();
  }, [calculateAndSetHeight]);

  // Observe ancestor size changes so the height stays correct when upstream
  // layout (for example a banner appearing after the initial render) changes.
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const node = ref.current;
    if (!node) {
      return undefined;
    }

    const observer = new ResizeObserver(() => {
      calculateAndSetHeight();
    });

    observer.observe(document.body);
    if (node.parentElement) {
      observer.observe(node.parentElement);
    }

    return () => observer.disconnect();
  }, [calculateAndSetHeight]);

  // Recalculate after initial render to get correct position.
  useEffect(() => {
    const timer = setTimeout(calculateAndSetHeight, 0);
    return () => clearTimeout(timer);
  }, [calculateAndSetHeight]);

  let style = { height };
  style = merge(style, styleProp);

  return (
    <div ref={ref} style={style} className={className}>
      {children({ height })}
    </div>
  );
};
